import useSWR, { mutate } from 'swr';
import { fetcher, fetcherWithMethod } from 'utils/axios';
import { handleApiError } from 'utils/errorHandler';
import { useMemo } from 'react';
import { CrudEndpoints } from 'types/common';
import { useUploadFileData } from 'api/commonCrud';
import { ContractorCompanyProfile } from 'types/contractor/companyProfile';

export const endpoints: CrudEndpoints = {
    key: 'Contractor/Settings/CurrentCompany',
    getList: '',
    getListById: '',
    getModal: '/GetCompany',
    getLookupList: '',
    insert: '/',
    update: '/UpdateCompany',
    delete: '',
    getlookupPost: true
};

export function useGetCompanyProfile() {
    const { data, isLoading, error, isValidating, mutate } = useSWR<ContractorCompanyProfile>(
        [`${endpoints.key}/${endpoints.getModal}`, { method: 'GET' }],
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err)
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const useUpdateCompanyProfile = async (data: ContractorCompanyProfile) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/${endpoints.update}`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export function useGetCompanyLogoUrl() {
    const { data, isLoading, error, isValidating, mutate } = useSWR<string>(
        `${endpoints.key}/GetCompanyLogoUrl`,
        (url: string) => fetcherWithMethod('GET', [url, { method: 'GET' }]),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err)
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export async function useUpdateCompanyLogo(file: File | null) {
    const result = await useUploadFileData<any>(
        `${endpoints.key}/UpdateCompanyLogo`,
        null,
        {
            File: file
        },
        { showSnackbar: false, method: 'POST' }
    );

    mutate(`${endpoints.key}/GetCompanyLogoThumbnailUrl`);

    return result;
}

export function useGetCompanyLogoThumbnailUrl() {
    const response = useSWR<string>(`${endpoints.key}/GetCompanyLogoThumbnailUrl`, fetcher, {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        onError: (err) => {
            handleApiError(err);
        }
    });

    return response
}